/**
 * @module features/upload/upload-constraints
 * @description Holds the upload limits reported by the API and validates files against them.
 *
 * The limits (allowed MIME types, maximum file size, maximum files per request) are
 * runtime configuration on the API side - Function App application settings, not code
 * constants - and can legitimately differ between staging and production. They are
 * therefore never hardcoded here: they arrive with the file categories and are cached
 * for the session.
 *
 * The server is the only authority. Anything held here exists purely to warn the user
 * before a doomed upload, so it must never be stricter than the server: where we cannot
 * be certain (an older API that reports no constraints, or a file whose type the browser
 * could not determine) we stay permissive and let the server reject, which it explains
 * per file.
 */

import { logger } from '../../utils/logger.js';

/**
 * Size limit applied only when the API reports none.
 * Matches the drop zone hint shown before any constraints have loaded.
 */
const FALLBACK_MAX_FILE_SIZE_MB = 10;

/**
 * Friendly labels for the MIME types we expect to see. Anything not listed is
 * shown as its raw MIME type rather than guessed at.
 */
const MIME_DISPLAY_NAMES = {
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/webp': 'WebP',
    'image/heic': 'HEIC',
    'application/pdf': 'PDF'
};

// Module state - null until the API reports constraints
let constraints = null;

/**
 * Store the constraints reported by the API.
 * Each field is validated independently so a partial payload is still useful.
 *
 * @param {Object} raw - The uploadConstraints object from the file-categories response
 */
export function setUploadConstraints(raw) {
    if (!raw || typeof raw !== 'object') {
        constraints = null;
        logger.debug('No upload constraints reported by API - client-side checks stay permissive');
        return;
    }

    const parsed = {};

    if (Array.isArray(raw.allowedMimeTypes)) {
        const types = raw.allowedMimeTypes
            .filter(type => typeof type === 'string' && type.trim())
            .map(type => type.trim().toLowerCase());

        // An empty list would block everything, which is never what the server means
        if (types.length > 0) {
            parsed.allowedMimeTypes = types;
        }
    }

    if (Number.isFinite(raw.maxFileSizeMb) && raw.maxFileSizeMb > 0) {
        parsed.maxFileSizeMb = raw.maxFileSizeMb;
    }

    if (Number.isFinite(raw.maxFilesPerRequest) && raw.maxFilesPerRequest > 0) {
        parsed.maxFilesPerRequest = raw.maxFilesPerRequest;
    }

    constraints = Object.keys(parsed).length > 0 ? parsed : null;

    logger.info('Upload constraints loaded from API', {
        allowedMimeTypeCount: constraints?.allowedMimeTypes?.length ?? 'unrestricted',
        maxFileSizeMb: constraints?.maxFileSizeMb ?? FALLBACK_MAX_FILE_SIZE_MB,
        maxFilesPerRequest: constraints?.maxFilesPerRequest ?? 'unrestricted'
    });
}

/**
 * Maximum file size in MB, falling back to the client default if unreported.
 * @returns {number} Maximum file size in MB
 */
export function getMaxFileSizeMb() {
    return constraints?.maxFileSizeMb ?? FALLBACK_MAX_FILE_SIZE_MB;
}

/**
 * Maximum files per request, or null if the API reported no limit.
 * @returns {number|null} Maximum files per request
 */
export function getMaxFilesPerRequest() {
    return constraints?.maxFilesPerRequest ?? null;
}

/**
 * Allowed MIME types, or null if the API reported none (meaning: do not restrict).
 * @returns {Array<string>|null} Lowercased MIME types
 */
export function getAllowedMimeTypes() {
    return constraints?.allowedMimeTypes ?? null;
}

/**
 * Value for the file input's accept attribute.
 * @returns {string} Accept attribute value
 */
export function getAcceptAttribute() {
    const allowed = getAllowedMimeTypes();
    return allowed ? allowed.join(',') : '*/*';
}

/**
 * Human-readable list of accepted types, for hints and error messages.
 * @returns {string} Comma-separated type names, or an empty string if unrestricted
 */
export function describeAllowedTypes() {
    const allowed = getAllowedMimeTypes();
    if (!allowed) {
        return '';
    }

    return allowed.map(type => MIME_DISPLAY_NAMES[type] || type).join(', ');
}

/**
 * Description of the current limits for the drop zone hint.
 * @returns {string} Hint text
 */
export function describeUploadConstraints() {
    const parts = [`Support for multiple files. Max ${getMaxFileSizeMb()}MB per file.`];

    const allowedTypes = describeAllowedTypes();
    if (allowedTypes) {
        parts.push(`Accepted types: ${allowedTypes}.`);
    }

    const maxFiles = getMaxFilesPerRequest();
    if (maxFiles) {
        parts.push(`Up to ${maxFiles} files per upload.`);
    }

    return parts.join(' ');
}

/**
 * Check a file against the known constraints.
 *
 * @param {File} file - File to check
 * @returns {{valid: boolean, reason?: string}} Result, with a reason when rejected
 */
export function validateFileAgainstConstraints(file) {
    const maxSizeMb = getMaxFileSizeMb();

    if (file.size > maxSizeMb * 1024 * 1024) {
        return {
            valid: false,
            reason: `"${file.name}" is too large. Maximum size is ${maxSizeMb}MB.`
        };
    }

    const allowed = getAllowedMimeTypes();

    // Only reject on type when the server has told us what it accepts and the browser
    // has given us a type to check. An empty file.type means the browser could not
    // determine it, so defer to the server rather than block a file it would accept.
    if (allowed && file.type && !allowed.includes(file.type.toLowerCase())) {
        return {
            valid: false,
            reason: `"${file.name}" is a ${file.type} file, which cannot be uploaded. Accepted types: ${describeAllowedTypes()}.`
        };
    }

    return { valid: true };
}
